<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import moment from 'moment';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiAvatar from '@/components/ui/UiAvatar.vue';
import UiBadgeList from '@/components/ui/UiBadgeList.vue';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { photoUrl } from '@/composables/useProfileSettings';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';
import { FACULTY_ROLE_CODES, PREFECT_ROLE_CODES, RoleCode } from '@/constants/accounts';
import { REPORT_SEVERITIES, reportDetails, reportSeverity, reportType } from '@/constants/reports';
import { formatDate, formatDateTime, formatTime, titleCase } from '@/utils/format';

useSessionGuard();

// Discipline records. A report is first "for resolution" with the student's
// class adviser, who resolves it or, if they can't settle it, escalates it
// to the Prefect of Discipline. The subject teacher who filed it only
// follows it here. The prefect resolves it (and may tell the parents) or
// asks the parents to a meeting, which they can accept, decline or move.
const store = useStore();
const router = useRouter();
const toast = useToast();
const roleCode = store.getters.activeRoleCode;
const isTeacher = FACULTY_ROLE_CODES.includes(roleCode);
const isPrefect = PREFECT_ROLE_CODES.includes(roleCode);
const isAdviser = roleCode === RoleCode.Adviser;

const TABS = isTeacher
  ? [
      { id: 1, name: 'For resolution' },
      { id: 2, name: 'Escalated' },
      { id: 3, name: 'Resolved' },
    ]
  : [
      { id: 1, name: 'For resolution' },
      { id: 4, name: 'Parent meeting' },
      { id: 3, name: 'Resolved' },
    ];
const tab = shallowRef(TABS[0].id);
const year = computed(() => store.getters.getFilterSelected);

const { items, loading, error, load } = useListResource(async () => {
  if (!isTeacher && !isPrefect) return [];
  const rows = isTeacher
    ? await apiRequest('get', `/parent-records/getDisciplinaryReport/${year.value}/${tab.value}/0`)
    : await apiRequest('get', `/parent-records/getPrefectReport/${year.value}/${tab.value}`);
  return (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch([tab, year], load, { immediate: true });

// Where a report is, in words.
const STAGES = {
  0: { text: 'For resolution', variant: 'amber' },
  1: { text: 'With the Prefect', variant: 'violet' },
  2: { text: 'With the Prefect', variant: 'violet' },
  5: { text: 'Parent meeting', variant: 'blue' },
  3: { text: 'Resolved', variant: 'green' },
  4: { text: 'Resolved by the Prefect', variant: 'green' },
};
const stageOf = (r) => STAGES[Number(r.status)] ?? STAGES[0];
const MEETING = {
  pending: { text: 'Waiting for the parent', variant: 'amber' },
  accepted: { text: 'Parent confirmed', variant: 'green' },
  declined: { text: 'Parent declined', variant: 'red' },
  cancelled: { text: 'Cancelled', variant: 'secondary' },
};
const meetingOf = (r) => (r.meeting_status ? MEETING[r.meeting_status] : null);

const headers = computed(() => [
  { title: 'Student', key: 'name' },
  { title: 'Reported by', key: 'teacher_name' },
  { title: 'Grade', key: 'grade_level' },
  { title: 'Type', key: 'report_type' },
  { title: 'Filed', key: 'created_at' },
  ...(tab.value === 4 ? [{ title: 'Meeting', key: 'meeting_schedule' }] : [{ title: 'Stage', key: 'status' }]),
  ...(isPrefect ? [{ title: 'Severity', key: 'severity' }] : []),
]);

// --- Review ---------------------------------------------------------------
const viewing = shallowRef(null);
const viewOpen = shallowRef(false);
const form = ref({});
const notifyParents = shallowRef(true);
const meetingOpen = shallowRef(false);
const showMeeting = computed(() => meetingOpen.value && canActAsPrefect.value);

const status = computed(() => Number(viewing.value?.status));
// The class adviser acts while it's with them (the server marks those rows);
// the prefect once escalated. Anyone else only views.
const canActAsTeacher = computed(() => isTeacher && status.value === 0 && !!viewing.value?.canHandle);
const canActAsPrefect = computed(() => isPrefect && [1, 2, 5].includes(status.value));
// Once resolved, whoever resolved it can still correct the record: the
// adviser their note, the prefect their note and attachment.
const canEditNotes = computed(
  () => (isTeacher && status.value === 3 && !!viewing.value?.canEditNotes) || (isPrefect && status.value === 4),
);
const editing = shallowRef(false);

const noteFields = computed(() => [
  {
    key: 'comments',
    label: canActAsPrefect.value || editing.value ? 'How was it resolved?' : 'Notes',
    type: 'textarea',
    // The browser mustn't refill it with an earlier note.
    autocomplete: 'off',
    required: true,
    description: editing.value
      ? 'Saving changes the note others see on this report. The parents are not notified again.'
      : canActAsPrefect.value
        ? 'Shared with the parents if you notify them.'
        : 'Required to resolve or escalate: what was done, or why it needs the Prefect of Discipline.',
  },
  // Whoever resolves it, adviser or Prefect, may attach a file with the
  // resolution. It stays with the resolution, so escalating doesn't take one.
  {
    key: 'file',
    label: 'Attachment',
    type: 'file',
    accept: ATTACHMENT_ACCEPT,
    rules: [attachmentRule],
    description:
      editing.value && viewing.value?.resolution_file_name
        ? 'Optional. Choose a file to replace the current one. A PDF, JPG or PNG of up to 1MB.'
        : canActAsTeacher.value
          ? 'Optional, kept with the resolution (not sent when escalating). A PDF, JPG or PNG of up to 1MB.'
          : 'Optional. A PDF, JPG or PNG of up to 1MB, e.g. a signed agreement.',
  },
]);
// The optional resolution file. The server checks the type and
// size too; this only says so before uploading.
const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const ATTACHMENT_MAX = 1024 * 1024;
const ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
// v-file-input gives a File or a list of them, depending on the version.
const chosenFile = () => {
  const v = form.value.file;
  return (Array.isArray(v) ? v[0] : v) || null;
};
function attachmentProblem(file) {
  if (!file) return '';
  if (!ATTACHMENT_TYPES.includes(file.type)) return 'Attach a PDF, JPG or PNG file.';
  if (file.size > ATTACHMENT_MAX) return 'The file must be 1MB or smaller.';
  return '';
}
const attachmentRule = (v) => attachmentProblem((Array.isArray(v) ? v[0] : v) || null) || true;
// A note goes with every resolve or escalation.
const hasNote = () => !!String(form.value.comments ?? '').trim();
const today = () => moment().format('YYYY-MM-DD');
const MEETING_FIELDS = [
  { key: 'date', label: 'Date', type: 'date', required: true, min: today() },
  { key: 'time', label: 'Time', type: 'time', required: true },
  { key: 'note', label: 'Message to the parents', type: 'textarea', span: 2, description: 'Sent to every parent asked. Edit it as needed.' },
];
// Starting text for the message. Parents already see which child it's about
// and the date and time, so it covers only why and what to bring.
const MEETING_NOTE_TEMPLATE =
  "Good day! We'd like to meet with you at the Prefect of Discipline's office to talk about the report and how we can support your child together. Please bring a valid ID. If you can't make it at this time, you can suggest another time.";

// Who a meeting request reaches: each student on the report (reported
// first, then those involved) with their linked parents, all ticked by
// default. Loaded when the prefect opens the meeting form.
const invitees = shallowRef([]);
const invitedIds = ref(new Set());
const loadingInvitees = shallowRef(false);
async function openMeeting() {
  meetingOpen.value = true;
  if (!form.value.note) form.value = { ...form.value, note: MEETING_NOTE_TEMPLATE };
  loadingInvitees.value = true;
  try {
    const rows = await apiRequest('get', `/parent-records/reports/${viewing.value.reportID}/parents`);
    invitees.value = Array.isArray(rows) ? rows : [];
    invitedIds.value = new Set(invitees.value.flatMap((s) => s.parents.map((p) => p.id)));
  } catch {
    invitees.value = [];
    toast.error('Could not load the parents.');
  } finally {
    loadingInvitees.value = false;
  }
}
function toggleInvite(id) {
  const next = new Set(invitedIds.value);
  next.has(id) ? next.delete(id) : next.add(id);
  invitedIds.value = next;
}
// One request per parent per child, so a parent of two students gets two.
const inviteCount = computed(() =>
  invitees.value.reduce((n, s) => n + s.parents.filter((p) => invitedIds.value.has(p.id)).length, 0),
);
const parentPhoto = (p) => (p.photo && p.photo !== 'img_avatar.png' ? photoUrl(p.photo) : '');
// Mobile, phone and email, whichever the parent has filled in.
const parentContacts = (p) =>
  [
    p.mobile && { icon: 'mdi-cellphone', text: p.mobile },
    p.phone && p.phone !== p.mobile && { icon: 'mdi-phone-outline', text: p.phone },
    p.email && { icon: 'mdi-email-outline', text: p.email },
  ].filter(Boolean);
const chosenFor = (s) => s.parents.filter((p) => invitedIds.value.has(p.id)).length;

// --- Severity --------------------------------------------------------------
// The Prefect classifies every report that reaches them, at any stage; it
// feeds the behavior summary on their dashboard. Others only see it.
const savingSeverity = shallowRef(false);
async function setSeverity(value) {
  const r = viewing.value;
  const next = value ?? null;
  if (!r || next === (r.severity ?? null)) return;
  savingSeverity.value = true;
  const ok = await run(() => apiRequest('patch', `/parent-records/reports/${r.reportID}/severity`, { severity: next }), {
    success: next ? `Marked as ${reportSeverity(next).name.toLowerCase()}.` : 'Severity cleared.',
    error: 'Could not save the severity.',
  });
  savingSeverity.value = false;
  if (ok) {
    viewing.value = { ...r, severity: next };
    load();
  }
}

function view(report) {
  viewing.value = report;
  form.value = { comments: '', date: '', time: '', note: '', file: null };
  notifyParents.value = true;
  editing.value = false;
  removeAttachment.value = false;
  meetingOpen.value = false;
  viewOpen.value = true;
}

const details = computed(() => {
  const r = viewing.value;
  if (!r) return [];
  return [
    { label: 'LRN', value: r.lrnNo },
    { label: 'Section', value: r.room_section },
    ...reportDetails(r),
  ];
});
// Each note under who wrote it, in its own section. The adviser's (how they
// resolved it, or why they escalated it) stays in view while the Prefect
// writes their own resolution, which starts empty.
const noteDetails = computed(() => {
  const r = viewing.value;
  if (!r) return [];
  // "Resolution · Juan Dela Cruz, Adviser"; just the role when the writer
  // isn't known (notes from before writers were recorded).
  const by = (name, role) => (name ? `${titleCase(name)}, ${role}` : role);
  return [
    ...(r.adviser_notes
      ? [
          {
            label: `${Number(r.status) === 3 ? 'Resolution' : 'Reason for escalating'} · ${by(r.adviser_notes_by_name, 'Adviser')}`,
            value: r.adviser_notes,
            span: 2,
          },
        ]
      : []),
    ...(r.prefect_notes
      ? [{ label: `Resolution · ${by(r.prefect_notes_by_name, 'Prefect of Discipline')}`, value: r.prefect_notes, span: 2 }]
      : []),
  ];
});
const meetingDetails = computed(() => {
  const r = viewing.value;
  if (!r || (!r.meeting_schedule && !r.meeting_date)) return [];
  return [
    { label: 'When', value: r.meeting_schedule ? formatDateTime(r.meeting_schedule) : `${formatDate(r.meeting_date)} ${formatTime(r.meeting_time)}` },
    { label: 'Parent', value: meetingOf(r)?.text ?? '—' },
    ...(r.meeting_notes ? [{ label: 'Message', value: r.meeting_notes, span: 2 }] : []),
  ];
});

const { busy, run } = useAsyncAction();
async function act(path, body, success) {
  const ok = await run(() => apiRequest(path.method, `/parent-records/reports/${viewing.value.reportID}/${path.action}`, body), {
    success,
    error: 'Could not update the report.',
  });
  if (ok) {
    viewOpen.value = false;
    load();
  }
}

function resolve() {
  if (!hasNote()) {
    toast.error('Write how it was resolved first.');
    return;
  }
  const file = chosenFile();
  const problem = attachmentProblem(file);
  if (problem) {
    toast.error(problem);
    return;
  }
  const fields = { comments: form.value.comments, notifyParents: canActAsPrefect.value && notifyParents.value };
  // With a file it goes as a multipart form, so the switch travels as text.
  let body = fields;
  if (file) {
    body = new FormData();
    body.append('comments', fields.comments);
    body.append('notifyParents', String(fields.notifyParents));
    body.append('file', file);
  }
  act(
    { method: 'patch', action: 'resolve' },
    body,
    canActAsPrefect.value && notifyParents.value ? 'Resolved. The parents have been told.' : 'Report resolved.',
  );
}
// The resolution attachment comes with the session token, so it is fetched
// and saved from here rather than linked to.
const downloading = shallowRef(false);
async function downloadAttachment() {
  downloading.value = true;
  try {
    const blob = await apiRequest('get', `/parent-records/reports/${viewing.value.reportID}/attachment`, undefined, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = viewing.value.resolution_file_name || 'attachment';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    // A failed blob request carries its JSON error as a Blob too.
    const body = e?.response?.data;
    if (body instanceof Blob) {
      try {
        e.response.data = JSON.parse(await body.text());
      } catch {
        // not JSON; fall back to the generic message
      }
    }
    toast.error(errorMessage(e, 'Could not download the attachment.'));
  } finally {
    downloading.value = false;
  }
}

// --- Editing a resolved report --------------------------------------------
const removeAttachment = shallowRef(false);
function startEdit() {
  const r = viewing.value;
  form.value = { ...form.value, comments: (status.value === 3 ? r.adviser_notes : r.prefect_notes) ?? '', file: null };
  removeAttachment.value = false;
  editing.value = true;
}
function saveNotes() {
  if (!hasNote()) {
    toast.error('Write how it was resolved first.');
    return;
  }
  const file = chosenFile();
  const problem = attachmentProblem(file);
  if (problem) {
    toast.error(problem);
    return;
  }
  let body = { comments: form.value.comments, removeAttachment: removeAttachment.value };
  if (file) {
    body = new FormData();
    body.append('comments', form.value.comments);
    body.append('file', file);
  }
  act({ method: 'patch', action: 'notes' }, body, 'Notes updated.');
}

function escalate() {
  if (!hasNote()) {
    toast.error('Write why it needs the Prefect of Discipline first.');
    return;
  }
  if (chosenFile()) {
    toast.error('An attachment goes with a resolution. Remove the file to escalate.');
    return;
  }
  act({ method: 'patch', action: 'escalate' }, { comments: form.value.comments }, 'Escalated to the Prefect of Discipline.');
}
function requestMeeting() {
  if (!form.value.date || !form.value.time) {
    toast.error('Choose the meeting date and time.');
    return;
  }
  if (!inviteCount.value) {
    toast.error('Choose at least one parent.');
    return;
  }
  act(
    { method: 'post', action: 'meeting' },
    { date: form.value.date, time: form.value.time, note: form.value.note, parentIds: [...invitedIds.value] },
    `Meeting request sent to ${inviteCount.value} parent${inviteCount.value === 1 ? '' : 's'}.`,
  );
}
</script>

<template>
  <UiTableCard
    v-model:tab="tab"
    title="Discipline records"
    :description="
      isAdviser
        ? 'Reports you filed or about your advisory class. Settle those about your advisory students, or escalate them to the Prefect of Discipline.'
        : isTeacher
          ? 'Reports you filed. The student\'s class adviser settles them or escalates them to the Prefect of Discipline.'
          : 'Reports escalated to you, and meetings with parents.'
    "
    :tabs="TABS"
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search reports"
    :empty-text="tab === 1 ? 'Nothing for resolution.' : 'No reports here.'"
    row-label="name"
    :row-actions="(row) => [{ title: 'View details', icon: 'mdi-file-document-outline', onClick: () => view(row) }]"
  >
    <template #[`item.name`]="{ item }">
      <UiPerson :name="item.name" :subtitle="[item.lrnNo && `LRN ${item.lrnNo}`, item.room_section].filter(Boolean).join(' · ')" />
    </template>
    <template #[`item.report_type`]="{ item }">
      <UiBadge :variant="reportType(item.report_type).variant">{{ reportType(item.report_type).name }}</UiBadge>
    </template>
    <template #[`item.created_at`]="{ item }">{{ formatDate(item.created_at) }}</template>
    <template #[`item.severity`]="{ item }">
      <UiBadge v-if="reportSeverity(item.severity)" :variant="reportSeverity(item.severity).variant">
        {{ reportSeverity(item.severity).name }}
      </UiBadge>
      <span v-else class="text-medium-emphasis">Not set</span>
    </template>
    <template #[`item.status`]="{ item }">
      <UiBadge :variant="stageOf(item).variant">{{ stageOf(item).text }}</UiBadge>
    </template>
    <template #[`item.meeting_schedule`]="{ item }">
      <span>{{ item.meeting_schedule ? formatDateTime(item.meeting_schedule) : '—' }}</span>
      <UiBadge v-if="meetingOf(item)" :variant="meetingOf(item).variant" class="ml-2">{{ meetingOf(item).text }}</UiBadge>
    </template>
  </UiTableCard>

  <UiDialog
    v-model="viewOpen"
    :title="viewing?.name ?? 'Report'"
    :description="viewing ? `${reportType(viewing.report_type).name} concern · ${viewing.grade_level}${viewing.room_section ? ` · ${viewing.room_section}` : ''}` : ''"
    :size="showMeeting ? '2xl' : 'lg'"
  >
    <!-- The meeting form opens beside the report, not below it -->
    <div v-if="viewing" class="review" :class="{ 'review--split': showMeeting }">
    <div class="form-stack review__main">
      <div class="d-flex flex-wrap ga-2">
        <UiBadge :variant="stageOf(viewing).variant">{{ stageOf(viewing).text }}</UiBadge>
        <UiBadge v-if="meetingOf(viewing)" :variant="meetingOf(viewing).variant">{{ meetingOf(viewing).text }}</UiBadge>
        <UiBadge v-if="!isPrefect && reportSeverity(viewing.severity)" :variant="reportSeverity(viewing.severity).variant">
          {{ reportSeverity(viewing.severity).name }} case
        </UiBadge>
      </div>

      <div v-if="isPrefect" class="severity">
        <div class="severity__head">
          <span class="text-body-2 text-medium-emphasis">Severity</span>
          <v-menu location="bottom start" :close-on-content-click="false" max-width="380">
            <template #activator="{ props: menu }">
              <v-btn v-bind="menu" icon="mdi-help-circle-outline" size="x-small" variant="text" aria-label="What the severity levels mean" />
            </template>
            <v-card class="severity__help">
              <p class="text-subtitle-2 mb-1">What the levels mean</p>
              <p class="text-body-2 text-medium-emphasis mb-3">
                Classify the case by what happened, not how far it has gone. It sets the behavior summary on your dashboard.
              </p>
              <div v-for="level in REPORT_SEVERITIES" :key="level.id" class="severity__level">
                <UiBadge :variant="level.variant">{{ level.name }}</UiBadge>
                <p class="text-body-2 mb-1 mt-1">{{ level.description }}</p>
                <p class="text-caption text-medium-emphasis mb-0">e.g. {{ level.examples }}</p>
              </div>
            </v-card>
          </v-menu>
        </div>
        <v-btn-toggle
          :model-value="viewing.severity ?? null"
          :disabled="savingSeverity"
          density="compact"
          variant="outlined"
          divided
          @update:model-value="setSeverity"
        >
          <v-btn v-for="level in REPORT_SEVERITIES" :key="level.id" :value="level.id" size="small">{{ level.name }}</v-btn>
        </v-btn-toggle>
        <p v-if="!viewing.severity" class="text-caption text-medium-emphasis mb-0">Not classified yet.</p>
      </div>
      <UiDetailList :items="details" />
      <div v-if="viewing.resolution_file_name">
        <p class="text-body-2 text-medium-emphasis mb-2">Attachment</p>
        <v-btn variant="outlined" size="small" prepend-icon="mdi-paperclip" :loading="downloading" @click="downloadAttachment">
          {{ viewing.resolution_file_name }}
        </v-btn>
      </div>
      <div>
        <p class="text-body-2 text-medium-emphasis mb-2">Other students involved</p>
        <UiBadgeList :items="viewing.tagged || []" empty="No other students tagged." />
      </div>

      <template v-if="noteDetails.length">
        <v-divider />
        <p class="text-subtitle-2 mb-0">Notes</p>
        <UiDetailList :items="noteDetails" />
      </template>

      <template v-if="meetingDetails.length">
        <v-divider />
        <p class="text-subtitle-2 mb-0">Parent meeting</p>
        <UiDetailList :items="meetingDetails" />
      </template>

      <template v-if="canActAsTeacher || canActAsPrefect || editing">
        <v-divider />
        <UiFormFields v-model:values="form" :fields="noteFields" />
        <v-checkbox
          v-if="editing && viewing.resolution_file_name && !chosenFile()"
          v-model="removeAttachment"
          :label="`Remove ${viewing.resolution_file_name}`"
          density="compact"
          hide-details
        />
        <v-switch
          v-if="canActAsPrefect"
          v-model="notifyParents"
          color="primary"
          label="Notify the parents when resolved"
          inset
          density="compact"
          hide-details
        />
      </template>

    </div>

      <aside v-if="showMeeting" class="form-stack review__side" aria-label="Parent meeting">
        <div class="review__side-head">
          <p class="text-subtitle-2 mb-0">{{ viewing.meeting_schedule ? 'Request a new meeting' : 'Ask the parents to a meeting' }}</p>
          <v-btn icon="mdi-close" size="small" variant="text" aria-label="Close the meeting form" @click="meetingOpen = false" />
        </div>
        <p class="text-body-2 text-medium-emphasis mb-0">
          The parents of everyone on this report are asked. Each can accept, decline, or move it to another time.
        </p>

        <div class="invitees">
          <div v-if="loadingInvitees" class="invitees__empty">Loading parents…</div>
          <section v-for="s in invitees" v-else :key="s.studentID" class="invitee" :aria-label="titleCase(s.studentName)">
            <div class="invitee__student">
              <div class="invitee__student-text">
                <span class="invitee__name">{{ titleCase(s.studentName) }}</span>
                <span class="invitee__meta">{{ [s.gradeLevel, s.section, s.lrnNo && `LRN ${s.lrnNo}`].filter(Boolean).join(' · ') }}</span>
              </div>
              <UiBadge :variant="s.reported ? 'amber' : 'secondary'">{{ s.reported ? 'Reported' : 'Involved' }}</UiBadge>
            </div>

            <template v-if="s.parents.length">
              <p class="invitee__count">
                {{ chosenFor(s) }} of {{ s.parents.length }} parent{{ s.parents.length === 1 ? '' : 's' }} will be asked
              </p>
              <label
                v-for="p in s.parents"
                :key="`${s.studentID}-${p.id}`"
                class="parent"
                :class="{ 'parent--on': invitedIds.has(p.id) }"
              >
                <input
                  :id="`invite-${s.studentID}-${p.id}`"
                  type="checkbox"
                  class="parent__check"
                  :checked="invitedIds.has(p.id)"
                  @change="toggleInvite(p.id)"
                />
                <UiAvatar :name="titleCase(p.name)" :src="parentPhoto(p)" :size="36" />
                <span class="parent__body">
                  <span class="parent__name">{{ titleCase(p.name) }}</span>
                  <span v-if="parentContacts(p).length" class="parent__contacts">
                    <span v-for="c in parentContacts(p)" :key="c.icon" class="parent__contact">
                      <v-icon size="14">{{ c.icon }}</v-icon>{{ c.text }}
                    </span>
                  </span>
                  <span v-else class="parent__contact parent__contact--none">No contact details on their account</span>
                </span>
              </label>
            </template>

            <p v-else class="invitee__none">
              <v-icon size="16">mdi-account-off-outline</v-icon>
              No parent account linked, so this family can't be asked here.
            </p>
          </section>
        </div>

        <div class="form-grid">
          <UiFormFields v-model:values="form" :fields="MEETING_FIELDS" />
        </div>
        <v-btn
          v-if="form.note !== MEETING_NOTE_TEMPLATE"
          variant="text"
          size="small"
          prepend-icon="mdi-restore"
          class="align-self-start"
          @click="form = { ...form, note: MEETING_NOTE_TEMPLATE }"
        >
          Use the template
        </v-btn>
      </aside>
    </div>

    <template #footer="{ close }">
      <v-btn variant="outlined" class="mr-auto" @click="close">Close</v-btn>
      <template v-if="canEditNotes">
        <v-btn v-if="!editing" variant="outlined" prepend-icon="mdi-pencil-outline" @click="startEdit">Edit notes</v-btn>
        <template v-else>
          <v-btn variant="text" :disabled="busy" @click="editing = false">Cancel</v-btn>
          <v-btn color="primary" :loading="busy" @click="saveNotes">Save</v-btn>
        </template>
      </template>
      <template v-if="canActAsTeacher">
        <v-btn variant="outlined" prepend-icon="mdi-arrow-up-bold-outline" :disabled="busy" @click="escalate">
          Escalate to Prefect
        </v-btn>
        <v-btn color="primary" :loading="busy" @click="resolve">Resolve</v-btn>
      </template>
      <template v-if="canActAsPrefect">
        <v-btn v-if="viewing?.meeting_schedule" variant="text" @click="router.push(router.currentRoute.value.path.replace(/[^/]+$/, 'appointments'))">
          Open in Appointments
        </v-btn>
        <v-btn v-if="!meetingOpen" variant="outlined" prepend-icon="mdi-calendar-account-outline" @click="openMeeting">
          {{ viewing?.meeting_schedule ? 'New meeting' : 'Parent meeting' }}
        </v-btn>
        <v-btn v-else color="primary" prepend-icon="mdi-send-outline" :loading="busy" :disabled="!inviteCount" @click="requestMeeting">
          Send to {{ inviteCount }} parent{{ inviteCount === 1 ? '' : 's' }}
        </v-btn>
        <!-- Hidden while scheduling: the prefect is meeting the parents, not resolving yet -->
        <v-btn v-if="!meetingOpen" color="primary" :loading="busy" @click="resolve">Resolve</v-btn>
      </template>
    </template>
  </UiDialog>
</template>

<style scoped>
.severity {
  display: grid;
  gap: 6px;
  justify-items: start;
}
.severity__head {
  display: flex;
  align-items: center;
  gap: 2px;
}
.severity__help {
  padding: 16px;
}
.severity__level + .severity__level {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}
.review {
  display: grid;
  gap: 24px;
}
.review--split {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
}
.review__side {
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--subtle);
}
.review__side-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: -4px -4px 0 0;
}
@media (max-width: 860px) {
  .review--split {
    grid-template-columns: minmax(0, 1fr);
  }
}
.invitees {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
}
.invitees__empty {
  padding: 12px;
  font-size: 13px;
  color: var(--muted-foreground);
}
.invitee {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
}
.invitee + .invitee {
  border-top: 1px solid var(--border);
}
.invitee__student {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.invitee__student-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.invitee__name {
  font-size: 14px;
  font-weight: 600;
}
.invitee__meta,
.invitee__count,
.invitee__none {
  font-size: 13px;
  color: var(--muted-foreground);
}
.invitee__count {
  margin: 0;
}

/* One linked parent: a selectable card with their contact details */
.parent {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--card);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.parent:hover {
  border-color: var(--border-strong);
}
.parent--on {
  border-color: var(--foreground);
  background: var(--subtle);
}
.parent:focus-within {
  box-shadow: 0 0 0 3px var(--ring-halo);
}
.parent__check {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: var(--foreground);
}
.parent__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.parent__name {
  font-size: 14px;
  font-weight: 500;
}
.parent__contacts {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
}
.parent__contact {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 13px;
  color: var(--muted-foreground);
  overflow-wrap: anywhere;
}
.parent__contact--none {
  font-style: italic;
}

/* No parent account */
.invitee__none {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 0;
}
</style>
